import { cn } from "@/lib/utils";
import { useEffect, useState, useMemo } from "react";
import { useI18N } from "common/hooks";
import { CaretDown } from "phosphor-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";

type ActionTitleFilterProps = {
  selectedNoteType: "Mapper" | "Public";
  setSelectedNoteType: (type: "Mapper" | "Public") => void;
  mapperNotesCount: number;
  publicNotesCount: number;
  showMapperView: boolean;
  onSortChange?: (sortOrder: number) => void;
  noteActions?: any[];
  venueNotes?: any[];
  selectedActionTitle?: string;
  setSelectedActionTitle?: (title: string) => void;
};

const ActionTitleFilter = ({
  selectedNoteType,
  setSelectedNoteType,
  mapperNotesCount,
  publicNotesCount,
  showMapperView,
  onSortChange,
  noteActions,
  venueNotes,
  selectedActionTitle,
  setSelectedActionTitle,
}: ActionTitleFilterProps) => {
  const i18n = useI18N();
  const [sortByText, setSortByText] = useState("Date added (Newest)");

  const sortOptions = useMemo(
    () => [
      {
        id: 1,
        label: "Date added (Newest)",
      },
      {
        id: 2,
        label: "Date added (Oldest)",
      },
      {
        id: 3,
        label: "Venue area",
      },
    ],
    []
  );

  useEffect(() => {
    if (onSortChange) {
      onSortChange(
        sortOptions.find((option) => option.label === sortByText)?.id || 1
      );
    }
  }, [sortByText, onSortChange, sortOptions]);

  useEffect(() => {
    // Auto-select the first action with data if nothing is selected
    if (
      !selectedActionTitle &&
      noteActions &&
      noteActions.length > 0 &&
      setSelectedActionTitle
    ) {
      setSelectedActionTitle(noteActions[0].title);
    }
  }, [noteActions, venueNotes, selectedActionTitle, setSelectedActionTitle]);

  // if (!showMapperView) return null;

  // if (
  //   selectedNoteType === "Public" &&
  //   (!noteActions || noteActions.length === 0)
  // ) {
  //   return null;
  // }

  return (
    <div className="flex flex-col">
      {showMapperView && (
        <div className="self-stretch py-2 inline-flex justify-start items-center">
          <div className="min-w-11 min-h-11 pr-4 py-2 inline-flex flex-col justify-center items-center overflow-hidden">
            <div className="text-center justify-center paragraph-small-medium text-primary-800 dark:text-primary-100">
              {i18n.get("noteFor")}
            </div>
          </div>
          <div className="py-2 flex justify-start items-center gap-2">
            {["Mapper", "Public"].map((tab) => (
              <button
                key={tab}
                className={cn(
                  "min-w-11 min-h-9 px-4 py-2 rounded-full flex justify-start items-center",
                  selectedNoteType === tab
                    ? "bg-secondary-400 dark:text-primary-900"
                    : "bg-primary-100 dark:bg-[#37354D]"
                )}
                onClick={() => setSelectedNoteType(tab as "Mapper" | "Public")}
                role="tab"
                aria-label={`${tab} ${selectedNoteType === tab ? "selected" : ""}`}
              >
                <div className="flex-1 text-center justify-start paragraph-small-medium">
                  {tab}
                  {tab === "Mapper" && ` (${mapperNotesCount})`}
                  {tab === "Public" && ` (${publicNotesCount})`}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedNoteType === "Public" &&
        noteActions &&
        noteActions.length > 0 && (
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Label and Sort - Always in a row */}
            <div className="flex flex-row items-center justify-between">
              <div className="min-w-11 min-h-11 pr-4 py-2 inline-flex flex-col justify-center items-center overflow-hidden">
                <div className="text-primary-800 dark:text-primary-300 paragraph-medium-semi-bold">
                  {i18n.get("publicNote")}
                </div>
              </div>

              <div className="md:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <div className="flex flex-row items-center gap-2 cursor-pointer">
                      <span className="border-b border-primary-800 paragraph-xsmall-medium">
                        {sortByText}
                      </span>
                      <CaretDown size={20} />
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="bg-primary-50 dark:bg-secondary-800 rounded-xl border border-[#cbc9f2] min-w-[200px]"
                    align="end"
                  >
                    <DropdownMenuLabel className="paragraph-xsmall-semibold text-left text-primary-500 dark:text-primary-150">
                      Sort by
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuRadioGroup
                      value={sortByText}
                      onValueChange={(value) => {
                        setSortByText(value);
                        const selectedOption = sortOptions.find(
                          (option) => option.label === value
                        );
                        if (selectedOption && onSortChange) {
                          onSortChange(selectedOption.id);
                        }
                      }}
                    >
                      {sortOptions.map((option) => (
                        <DropdownMenuRadioItem
                          key={option.id}
                          value={option.label}
                          className="hover:bg-gray-200 focus:bg-gray-200 dark:hover:bg-primary-700 dark:focus:bg-primary-700 rounded-md mx-2 my-1"
                        >
                          {option.label}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Note Actions - Full width on mobile, inline on desktop */}
            <div className="w-full md:w-80 p-1 rounded-full inline-flex justify-start items-center gap-1 border border-primary-150">
              {noteActions.map((action, i) => (
                <button
                  key={i}
                  className="flex-1"
                  onClick={() => setSelectedActionTitle?.(action.title)}
                >
                  <div
                    className={cn(
                      "flex-1 rounded-full h-full items-center justify-center min-w-11 min-h-9 px-4 py-2",
                      selectedActionTitle === action.title && "bg-secondary-400"
                    )}
                  >
                    <span className="paragraph-small-medium text-primary-800 dark:text-primary-300">
                      {`${action.title} (${
                        venueNotes?.filter((note) => {
                          if (!note || typeof note !== "object") return false;
                          const actionTitle =
                            note.action_title ||
                            (note.note && note.note.action_title) ||
                            (note.details && note.details.action_title);
                          return actionTitle === action.title;
                        }).length || 0
                      })`}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {/* Sort Dropdown - Hidden on mobile, shown on desktop */}
            <div className="hidden md:block ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <div className="flex flex-row items-center gap-2 cursor-pointer">
                    <span className="border-b border-primary-800 paragraph-xsmall-medium">
                      {sortByText}
                    </span>
                    <CaretDown size={20} />
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="bg-primary-50 dark:bg-secondary-800 rounded-xl border border-[#cbc9f2] min-w-[200px]"
                  align="end"
                >
                  <DropdownMenuLabel className="paragraph-xsmall-semibold text-left text-primary-500 dark:text-primary-150">
                    Sort by
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuRadioGroup
                    value={sortByText}
                    onValueChange={(value) => {
                      setSortByText(value);
                      const selectedOption = sortOptions.find(
                        (option) => option.label === value
                      );
                      if (selectedOption && onSortChange) {
                        onSortChange(selectedOption.id);
                      }
                    }}
                  >
                    {sortOptions.map((option) => (
                      <DropdownMenuRadioItem
                        key={option.id}
                        value={option.label}
                        className="hover:bg-gray-200 focus:bg-gray-200 dark:hover:bg-primary-700 dark:focus:bg-primary-700 rounded-md mx-2 my-1"
                      >
                        {option.label}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}
    </div>
  );
};

export default ActionTitleFilter;
